// frontend/src/serviceWorkerRegistration.js
// Registers public/service-worker.js — see that file's own header for the
// full "app shell only, never the API" caching strategy and why. This
// module is deliberately just plumbing: register the worker, and surface
// "a new version is waiting" through a tiny pub/sub so any component
// (see useServiceWorkerUpdate.js) can show a banner, without this file
// knowing anything about React.
//
// Skipped entirely on localhost / non-HTTPS — a service worker requires a
// secure context anyway (browsers refuse to register one over plain
// http://), and registering one against CRA's dev server would just cache
// stale dev builds while iterating locally, exactly the opposite of what
// anyone wants while developing.

const listeners = new Set();

export function onUpdateAvailable(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notifyUpdateAvailable(registration) {
  for (const cb of listeners) cb(registration);
}

export function register() {
  if (!('serviceWorker' in navigator)) return;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;
  if (window.location.protocol !== 'https:') return;

  window.addEventListener('load', () => {
    // Captured BEFORE registration — true only when this tab already had a
    // controller (i.e. was already running under a PREVIOUS service worker)
    // at load time. Used below to tell a genuine version swap apart from
    // clients.claim() taking control for the very first time.
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;

    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        // A worker already waiting the moment registration resolves (the
        // tab was opened AFTER a newer deploy's worker had already
        // installed in a previous visit, but this page load is still
        // running the old controller) — same signal as the updatefound
        // path below, just a different timing.
        if (registration.waiting) {
          notifyUpdateAvailable(registration);
        }
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // installed + there's an existing controller (this isn't the
            // very first install) means a NEWER version finished
            // installing in the background while an older one is still
            // actively serving this tab.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              notifyUpdateAvailable(registration);
            }
          });
        });
      })
      .catch(() => {
        // Registration failing (e.g. the file 404s, or a transient
        // network hiccup) must never block the app itself — it simply
        // runs without the caching speed-up this session, same as today.
      });

    // A second tab's reload-after-update calling skipWaiting() flips
    // navigator.serviceWorker.controller out from under THIS tab too —
    // reload once to pick up the new version consistently everywhere,
    // rather than leaving a second tab silently running stale JS against
    // a newer API.
    //
    // BUG FIXED (2026-10-02, found live — "occasionally getting this blank
    // screen"): controllerchange ALSO fires the very first time a worker
    // takes control via clients.claim() in service-worker.js's activate
    // handler — i.e. on a brand-new visit, or any time a tab had no
    // controller yet (first load after a hard refresh/cache clear/private
    // window). That's not a version swap at all; the page already has the
    // correct, freshly-downloaded JS running in memory. Unconditionally
    // reloading there served no purpose and could race (e.g. reload
    // firing while the app was still finishing its own initial render),
    // occasionally landing on a blank page. Now only reloads when this
    // tab ALREADY had a controller at load time — a genuine old-version
    // -> new-version swap — never on first-ever activation.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadControllerAtLoad) return;
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

// Tells the waiting worker to activate now — triggers the controllerchange
// listener above, which reloads the page onto the new version. Called from
// the update banner's own "Refresh" button (useServiceWorkerUpdate.js),
// never automatically: an unattended reload mid-sale/mid-checkin would be
// its own real problem.
export function activateWaitingWorker(registration) {
  if (registration?.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}
